import React, { useEffect, useState } from 'react';
import './CalcCeDataTable.css';

import { useUserContext } from '../../contexts/UserContext';

import {
    CellContext,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { CalcCeResult, ResultCe } from './CalcCeData';

const defaultData: ResultCe[] = [
];

const columnHelper = createColumnHelper<ResultCe>()

// Nullを赤文字エラーに設定
function disp(value:string | null | undefined) {
    var className = '';
    if (null == value || undefined == value) {
        value = 'error';
        className = 'error';
    }
    return <span className={className}>{value}</span>;
}
// 日時の表示形式設定
function dispDayAndTime(info:CellContext<ResultCe,string|undefined>) {
    var value = info.getValue()?.replaceAll('-','/')?.substring(0, 16);
    return value;
}
// // 数値の表示形式設定
function dispValue(info:CellContext<ResultCe,number|undefined>) {
    var value = info?.getValue()?.toLocaleString(
            undefined,
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        );
    return value;
}
// 対数の表示形式設定
function dispNumOfCell(info:CellContext<ResultCe,number|undefined>) {
    var value = info?.getValue()?.toLocaleString(
            undefined,
            {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }
        );
    return value;
}

const columns = [
    // 日時
    columnHelper.accessor('calculationDt', {
        header: () => 'Day and time',
        cell: info => {
            return disp(dispDayAndTime(info));
        }
    }),
    // CE
    columnHelper.accessor('ce', {
        header: () => <span>CE<br/>[%]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 時間平均3
    columnHelper.accessor('ceAvg1', {
        header: () => <span>CE<br/>av. 24H<br/>[%]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 時間平均4
    columnHelper.accessor('ceAvg2', {
        header: () => <span>CE<br/>av. 48H<br/>[%]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 計算結果CE（72H）
    columnHelper.accessor('ceAvg3', {
        header: () => <span>CE<br/>av. 72H<br/>[%]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 計算結果CE（96H）
    columnHelper.accessor('ceAvg4', {
        header: () => <span>CE<br/>av. 96H<br/>[%]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 電解電流
    columnHelper.accessor(row => row.electrolyticCurrent, {
        id: 'electrolyticCurrent',
        header: () => <span>Current<br/>[kA]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 電圧
    columnHelper.accessor('voltage', {
        header: () => <span>Electrolyzer<br/>Voltage<br/>[V]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 苛性温度
    columnHelper.accessor('causticTemperature', {
        header: () => <span>Catholyte<br/>Outlet<br/>Temp.<br/>[degC]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 塩水出口ＰＨ
    columnHelper.accessor('saltWaterOutletPh', {
        header: () => <span>Anolyte<br/>Outlet pH</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 塩素ガス圧力
    columnHelper.accessor('chGasPressure', {
        header: () => <span>Cl2<br/>pressure<br/>[kPa]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 塩酸流量
    columnHelper.accessor('hclFlowRate', {
        header: () => <span>HCl<br/>flow rate<br/>[L/hr]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 塩水流量
    columnHelper.accessor('salWaterFlow', {
        header: () => <span>Brine<br/>flow rate<br/>[m3/hr]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 塩酸濃度
    columnHelper.accessor('hclConcentration', {
        header: () => <span>HCl<br/>Conc.<br/>[wt%]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // SO4濃度
    columnHelper.accessor('so4Concentration', {
        header: () => <span>SO4 Conc.<br/>(av. in <br/>purified<br/>brine)<br/>[g/L]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // pH
    columnHelper.accessor('ph', {
        header: () => <span>Purified<br/>brine pH</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 苛性濃度
    columnHelper.accessor('causticConcentration', {
        header: () => <span>Catholyte<br/>Outlet<br/>Conc.<br/>[wt%]</span>,
        cell: info => {
            return disp(dispValue(info));
        },
    }),
    // 対数
    columnHelper.accessor('cells', {
        header: () => <span>Number of<br/>cell</span>,
        cell: info => {
            return disp(dispNumOfCell(info));
        },
    }),
]

type Props = {
    calcResult: CalcCeResult,
}

export const CalcCeDataTable: React.FC<Props> = ({calcResult}) => {

    const [data, setData] = React.useState<ResultCe[]>(() => [...defaultData]);
    const [loading, setLoading] = useState<boolean>(false);
    const userId = useUserContext().userName;

    useEffect(() => {
        console.log('CalcCeDataTable: update. resultCe.length=' + calcResult.resultListCe?.length);
        const resultCe: ResultCe[] = calcResult.resultListCe ? calcResult.resultListCe : defaultData;
        setData(resultCe);
        console.log('CalcCeDataTable: update. data.length=' + data.length);
    }, [calcResult]);

    const validData = data.filter(element => element.ce !== undefined);
    const inValidData = data.filter(element => element.ce === undefined);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    })

    const click = (event: React.MouseEvent<HTMLButtonElement>) => {
        setLoading(true);
        const arrayData : any[] = [];
        const headerData: any[] = [];

        // 画面表示と同じ順でヘッダを配列化
        const tableTag = document.querySelector('.CalcCeDataTable_table table');
        const trTags = tableTag?.querySelectorAll('tr');
        trTags?.forEach(
            (trTag, index) => {
                const rowData : any[] = [];
                // 1行目＝ヘッダはthタグ
                if (0 == index){
                    const thTags = trTag.querySelectorAll('th');
                    thTags.forEach(
                        (thTag) => {
                            rowData.push('"' + thTag.innerText.replace(/\n/g,' ') + '"');
                        }
                    );
                }
                // 2行目以降、日時はthタグ、日時以外はtdタグ
                if (0 < index) {
                    const thTag = trTag.querySelector('th');
                    rowData.push(thTag?.innerText)

                    const tdTags = trTag.querySelectorAll('td');
                    tdTags.forEach(
                        (tdTag) => {
                            rowData.push(tdTag.innerText.replace(/,/g, ''));
                        }
                    );
                }
                arrayData.push(rowData);
            }
        );

        // 文字コード（UTF8）
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        // ２次元配列をCSV文字列に変換
        let csvData = arrayData.map(function(row){return row.join(',')}).join('\r\n');
        // BLOB生成してダウンロード実行
        const blob = new Blob([bom, csvData], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const dummyLink = document.createElement("a");
        dummyLink.type = 'application/octet-stream';
        
        // ファイル名設定用の日時取得
        const today = new Date();
        const year = today.getFullYear();
        const month = (today.getMonth()+1).toLocaleString().padStart(2, '0');
        const date = today.getDate().toLocaleString().padStart(2, '0');
        const hour = today.getHours().toLocaleString().padStart(2, '0');
        const minutes= today.getMinutes().toLocaleString().padStart(2, '0');
        const dayAndTime = year + month + date + hour + minutes;

        const fileName = 'CE_Calculation-'+ userId +'-'+ calcResult.electrolyzer +'-'+ dayAndTime +'.csv';
        dummyLink.download = fileName;
        dummyLink.href = url;
        dummyLink.click();

        setLoading(false);
    }

    return (
        <div className="CalcCeDataTable_content">
            <div className='CalcCeDataTable_child_content'>
                <button
                    type="button"
                    className="CalcCeDataTable_download_button"
                    onClick={click}
                >
                    Download
                    {
                        loading &&
                        (
                            <div className="Common_loader"></div>
                        )
                    }                
                </button>
                <table className='CalcCeDataTable_reference'>
                    <tr>
                        <th>Excel Last Row</th>
                        <th>Valid Data</th>
                        <th>Invalid Data</th>
                        <th className='Calculation_Mode'>Calculation Mode</th>
                    </tr>
                    <tr>
                        <td>{(calcResult.excelLastRow)}</td>
                        <td>{validData.length}</td>
                        <td>{inValidData.length}</td>
                        <td className='Calculation_Mode'>{(calcResult.coefficientSetDisplayName)}</td>
                    </tr>
                </table>
            </div>
                
            <div className="CalcCeDataTable_table">
                <table>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id}
                                >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell, index) =>
                                index == 0 ?
                                (
                                    <th key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </th>

                                ) :
                                (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                </table>
            </div>
        </div>
    );
};
